import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppRoleService, AppService } from '@nk/access-block';
import { WATCHLIST_API_SERVICE_TOKEN } from '@nk/api-lib';
import {
  AppIdentityCompositeEntity,
  AppRoleDims,
  PartialCompanyByUuid,
  WatchlistEntity,
  WithUuid,
} from '@nk/data-entities-lib';
import { ButtonDirective, ConfirmOrCancelModalComponent, NkToastrService, SpinnerComponent } from '@nk/ui-lib';
import { NkChanges } from '@nk/utils-lib';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { WatchlistCreateModalComponent } from '../watchlist-create-modal/watchlist-create-modal.component';
import { filterWatchlistOptions } from './add-to-watchlist-dropdown.utils';

@Component({
  selector: 'nk-add-to-watchlist-dropdown',
  imports: [AsyncPipe, TranslateModule, BsDropdownModule, ButtonDirective, SpinnerComponent],
  templateUrl: './add-to-watchlist-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddToWatchlistDropdownComponent implements OnInit, OnChanges {
  @Input() public inputWatchlistOptions!: WithUuid<WatchlistEntity>[];
  @Input() public companyUuid!: string;
  @Input() public label = extractTranslation('watchlists.add_to_watchlist_dropdown.add_company_to_watchlist');
  @Input() public hideCreateLink = false;
  @Output() public createWatchlistEvent = new EventEmitter<WithUuid<WatchlistEntity>>();
  @Output() public addToWatchlistEvent = new EventEmitter<WithUuid<WatchlistEntity>>();

  public watchlistOptions$!: Observable<WithUuid<WatchlistEntity>[]>;
  private _triggerWatchlistOptionsUpdate$ = new BehaviorSubject(null);
  public identity$!: Observable<AppIdentityCompositeEntity>;
  public roleDim$!: Observable<AppRoleDims>;
  private _watchlistService = inject(WATCHLIST_API_SERVICE_TOKEN);
  private _translateService = inject(TranslateService);
  private _toastr = inject(NkToastrService);
  private _modalService = inject(BsModalService);
  private _appService = inject(AppService);
  private _appRoleService = inject(AppRoleService);
  private _destroy = inject(DestroyRef);

  public ngOnInit(): void {
    this.identity$ = this._appService.identityDataCache.get();
    this.roleDim$ = this._appRoleService.appRoleListByMembershipCache.get().pipe(map((r) => new AppRoleDims(r)));

    this.watchlistOptions$ = this._triggerWatchlistOptionsUpdate$.pipe(
      switchMap(() =>
        combineLatest([this.identity$, this.roleDim$]).pipe(
          switchMap(([identity, roleDim]) =>
            this._watchlistService
              .fetchWatchlistsByCompanyAndCustomer(identity.app_customer.uuid, this.companyUuid)
              .pipe(
                map((watchlistsByCompany) =>
                  filterWatchlistOptions(identity, roleDim, watchlistsByCompany, this.inputWatchlistOptions),
                ),
              ),
          ),
        ),
      ),
    );
  }

  public ngOnChanges(changes: NkChanges<AddToWatchlistDropdownComponent>): void {
    if (changes.inputWatchlistOptions?.currentValue) {
      this._triggerWatchlistOptionsUpdate$.next(null);
    }
  }

  public onCreateWatchlist(): void {
    const initialState = {
      companyUuidList: [this.companyUuid],
    };
    const watchlistCreateModalRef = this._modalService.show(WatchlistCreateModalComponent, {
      initialState,
      ignoreBackdropClick: true,
    });
    watchlistCreateModalRef.onHide
      ?.pipe(
        map(() => watchlistCreateModalRef.content?.createdWatchlist()),
        takeUntilDestroyed(this._destroy),
      )
      .subscribe((watchlist) => {
        if (watchlist) {
          this.createWatchlistEvent.emit(watchlist);
        }
      });
  }

  public onAddToWatchlist(watchlist: WithUuid<WatchlistEntity>): void {
    this._watchlistService
      .getInsertWarningForWatchlist(watchlist.uuid)
      .pipe(
        map((a) => a.filter((message) => !!message)),

        switchMap((warning) => {
          if (warning && warning.length) {
            const warningText = warning.join(', ');

            const modalRef = this._modalService.show(ConfirmOrCancelModalComponent, {
              initialState: {
                text: warningText,
                title: this._translateService.instant(
                  extractTranslation('company_page.watchlist_card.add_company_to_watchlist'),
                ) as string,
                mainBtnLabel: this._translateService.instant(
                  extractTranslation('company_page.watchlist_card.modal_add_to_watchlist'),
                ) as string,
                mainBtnColor: 'danger',
              },
            });
            if (!modalRef.onHide) {
              return of(false);
            }
            return modalRef.onHide?.pipe(map(() => modalRef.content?.isConfirmed ?? false));
          } else {
            return of(true);
          }
        }),
      )
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.addToWatchlist(watchlist);
        }
      });
  }

  public addToWatchlist(watchlist: WithUuid<WatchlistEntity>): void {
    // Remove from dropdown
    this._triggerWatchlistOptionsUpdate$.next(null);

    // do service stuff
    const partialCompany: PartialCompanyByUuid = { uuid: this.companyUuid };
    this._watchlistService
      .postCompanyToWatchlist(watchlist.uuid, partialCompany)
      .pipe(
        tap((company) => {
          if (company) {
            this.addToWatchlistEvent.emit(watchlist);
            this._toastr.success(
              this._translateService.instant(
                extractTranslation('watchlists.add_to_watchlist_dropdown.add_company.success_message'),
                { companyName: company.name, watchlistName: watchlist.name },
              ),
              this._translateService.instant(
                extractTranslation('watchlists.add_to_watchlist_dropdown.add_company.success_message_title'),
              ),
            );
          } else {
            this._toastr.warning(
              this._translateService.instant(
                extractTranslation('watchlists.add_to_watchlist_dropdown.add_company.error_message'),
              ),
            );
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this._toastr.warning(
            this._translateService.instant(
              extractTranslation('watchlists.add_to_watchlist_dropdown.add_company.error_message'),
            ),
          );
          return throwError(() => error);
        }),
        takeUntilDestroyed(this._destroy),
      )
      .subscribe();
  }
}
